import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload,
  Download,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  FileText, 
  Loader, 
  X, 
  Database, 
  TrendingUp,
  Package
} from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { showNotification, showError } from '../../utils/notificationHandler';
import * as XLSX from 'xlsx';
import PageHeader from '../shared/PageHeader';
import Button from '../shared/Button';
import Card from '../shared/Card';

const PriceScheduleUpload = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [selectedSchedule, setSelectedSchedule] = useState('');
  const [scheduleStats, setScheduleStats] = useState(null);
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);

  // Fetch available price schedules
  const fetchSchedules = useCallback(async () => {
    setLoadingSchedules(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        showError(new Error('Not authenticated'), 'Please log in to continue');
        return;
      }

      const { data, error } = await supabase.functions.invoke('upload-price-schedule', {
        body: {},
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (error) throw error;

      if (data?.success) {
        setSchedules(data.schedules || []);
        
        // Auto-select first schedule if available
        if (data.schedules && data.schedules.length > 0 && !selectedSchedule) {
          setSelectedSchedule(data.schedules[0].id.toString());
        }
      } else {
        throw new Error(data?.error || 'Failed to fetch schedules');
      }
    } catch (error) {
      console.error('Error fetching schedules:', error);
      showError(error, 'Failed to fetch price schedules');
    } finally {
      setLoadingSchedules(false);
    }
  }, [selectedSchedule]);

  // Fetch statistics for selected schedule
  const fetchScheduleStats = useCallback(async (scheduleId) => {
    if (!scheduleId) return;
    
    setLoadingStats(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      const { data, error } = await supabase.functions.invoke('upload-price-schedule', {
        body: { action: 'schedule-stats', schedule_id: scheduleId },
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (error) throw error;

      if (data?.success) {
        setScheduleStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching schedule stats:', error);
    } finally {
      setLoadingStats(false);
    }
  }, []);

  // Load schedules on component mount
  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  // Load stats when schedule changes
  useEffect(() => {
    if (selectedSchedule) {
      fetchScheduleStats(selectedSchedule);
    }
  }, [selectedSchedule, fetchScheduleStats]);

  // Handle file selection
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.name.endsWith('.csv') || selectedFile.name.endsWith('.CSV')) {
        setFile(selectedFile);
        setUploadResult(null);
      } else {
        showError(new Error('Invalid file type'), 'Please select a CSV file');
      }
    }
  };

  // Handle file upload
  const handleUpload = async () => {
    if (!file) {
      showError(new Error('No file selected'), 'Please select a file to upload');
      return;
    }

    if (!selectedSchedule) {
      showError(new Error('No schedule selected'), 'Please select a price schedule');
      return;
    }

    setUploading(true);
    setUploadResult(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      // Create form data
      const formData = new FormData();
      formData.append('file', file);
      formData.append('schedule_id', selectedSchedule);

      const { data, error } = await supabase.functions.invoke('upload-price-schedule', {
        body: formData,
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (error) throw error;

      setUploadResult(data);

      if (data.success) {
        showNotification(
          `Successfully synced ${data.total_processed} products! (${data.total_inserted} inserted/updated)`,
          'success'
        );
        // Refresh stats
        fetchScheduleStats(selectedSchedule);
      } else {
        showError(
          new Error('Upload completed with errors'),
          `${data.total_processed} processed, ${data.total_errors} errors`
        );
      }

    } catch (error) {
      console.error('Upload error:', error);
      showError(error, 'Failed to upload price schedule');
      setUploadResult({
        success: false,
        error: error.message
      });
    } finally {
      setUploading(false);
    }
  };

  // Download template
  const downloadTemplate = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const { data, error } = await supabase.functions.invoke('upload-price-schedule', {
        body: { action: 'template' },
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (error) throw error;

      // Create blob from CSV text
      const blob = new Blob([data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'price_schedule_template.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      showNotification('Template downloaded successfully', 'success');
    } catch (error) {
      console.error('Download error:', error);
      showError(error, 'Failed to download template');
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <PageHeader
        title="Price Schedules"
        description="Select a price schedule and upload a CSV to insert or update its product prices."
      />

      <Card>

        {/* Schedule Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Price Schedule
          </label>
          {loadingSchedules ? (
            <div className="flex items-center text-gray-500">
              <Loader className="w-4 h-4 mr-2 animate-spin" />
              Loading schedules...
            </div>
          ) : (
            <select
              value={selectedSchedule}
              onChange={(e) => setSelectedSchedule(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand"
            >
              <option value="">-- Select a schedule --</option>
              {schedules.map((schedule) => (
                <option key={schedule.id} value={schedule.id}>
                  {schedule.name} ({schedule.product_count} products)
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Current Statistics */}
        {scheduleStats && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
            <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
              <Database className="w-5 h-5 mr-2 text-gray-500" />
              Current Schedule Statistics
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg p-3 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Products</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {scheduleStats.product_count.toLocaleString()}
                    </p>
                  </div>
                  <Package className="w-8 h-8 text-gray-400" />
                </div>
              </div>
              
              {scheduleStats.price_stats?.avg_price && (
                <div className="bg-white rounded-lg p-3 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Avg Price</p>
                      <p className="text-2xl font-bold text-gray-900">
                        ${scheduleStats.price_stats.avg_price.toFixed(2)}
                      </p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-gray-400" />
                  </div>
                </div>
              )}
              
              <div className="bg-white rounded-lg p-3 border border-gray-200">
                <div>
                  <p className="text-sm text-gray-600">Last Updated</p>
                  <p className="text-sm font-medium text-gray-800">
                    {scheduleStats.last_updated 
                      ? new Date(scheduleStats.last_updated).toLocaleDateString()
                      : 'Never'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Upload Section */}
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 mb-6 text-center">
          <div className="space-y-4">
            <div className="flex justify-center">
              <Upload className="w-16 h-16 text-gray-400" />
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Upload Price Schedule CSV</h3>
              <p className="text-gray-600 mb-4">
                Upload a CSV file to sync product prices with your ERP system
              </p>
              
              <input
                type="file"
                id="file-upload"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
              />
              
              <div className="space-y-3">
                <label
                  htmlFor="file-upload"
                  className="inline-flex items-center px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg text-sm font-medium cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <FileText className="w-5 h-5 mr-2" />
                  Choose CSV File
                </label>
                
                {file && (
                  <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                    <FileText className="w-4 h-4" />
                    <span>{file.name}</span>
                    <button
                      onClick={() => setFile(null)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <Button
              size="lg"
              onClick={handleUpload}
              disabled={!file || !selectedSchedule}
              loading={uploading}
              icon={<Upload className="w-5 h-5" />}
            >
              {uploading ? 'Uploading & Syncing...' : 'Upload & Sync Prices'}
            </Button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-4 justify-center mb-6">
          <Button
            variant="secondary"
            onClick={downloadTemplate}
            icon={<Download className="w-5 h-5" />}
          >
            Download CSV Template
          </Button>
          
          <Button
            variant="secondary"
            onClick={() => fetchScheduleStats(selectedSchedule)}
            disabled={!selectedSchedule}
            loading={loadingStats}
            icon={<Database className="w-5 h-5" />}
          >
            Refresh Statistics
          </Button>
        </div>

        {/* Upload Results */}
        <AnimatePresence>
          {uploadResult && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mt-6"
            >
              <div className={`rounded-lg p-4 ${
                uploadResult.success 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-start">
                  {uploadResult.success ? (
                    <CheckCircle className="w-6 h-6 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-6 h-6 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                  )}
                  <div className="flex-1">
                    <h4 className={`font-semibold ${
                      uploadResult.success ? 'text-green-800' : 'text-red-800'
                    }`}>
                      {uploadResult.success ? 'Sync Successful!' : 'Sync Failed'}
                    </h4>
                    
                    {uploadResult.success ? (
                      <div className="mt-2 text-sm text-green-700 space-y-1">
                        <p className="flex items-center gap-1"><CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" aria-hidden="true" />Processed: {uploadResult.total_processed} products</p>
                        <p className="flex items-center gap-1"><CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" aria-hidden="true" />Inserted/Updated: {uploadResult.total_inserted} products</p>
                        <p className="flex items-center gap-1"><CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" aria-hidden="true" />Processing time: {uploadResult.processing_time_seconds}s</p>
                        {uploadResult.total_errors > 0 && (
                          <p className="text-orange-600 flex items-center gap-1"><AlertTriangle className="h-4 w-4 text-orange-500 flex-shrink-0" aria-hidden="true" />Errors: {uploadResult.total_errors}</p>
                        )}
                      </div>
                    ) : (
                      <div className="mt-2 text-sm text-red-700">
                        <p>{uploadResult.error}</p>
                      </div>
                    )}

                    {/* Show errors if any */}
                    {uploadResult.errors && uploadResult.errors.length > 0 && (
                      <div className="mt-3">
                        <h5 className="font-medium text-red-800">Errors:</h5>
                        <ul className="mt-1 text-sm text-red-700 list-disc list-inside max-h-32 overflow-y-auto">
                          {uploadResult.errors.slice(0, 10).map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                          {uploadResult.errors.length > 10 && (
                            <li>... and {uploadResult.errors.length - 10} more errors</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Instructions */}
        <div className="mt-6 bg-gray-50 rounded-lg p-4">
          <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
            <AlertCircle className="w-5 h-5 mr-2 text-gray-500" />
            Instructions
          </h4>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• Select a price schedule from the dropdown</li>
            <li>• File must be in CSV format (.csv)</li>
            <li>• Required column: <code className="bg-gray-200 px-1 rounded">product_code</code></li>
            <li>• Optional columns: product_group, description, price_code, value, reference_price, result, etc.</li>
            <li>• The system will automatically insert new products or update existing ones</li>
            <li>• Products are matched by schedule_id + product_code combination</li>
            <li>• Download the template file to see the correct format and all available columns</li>
            <li>• Large files are processed in batches for better performance</li>
          </ul>
        </div>
      </Card>
    </div>
  );
};

export default PriceScheduleUpload;
